import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import {
  MultiSelect,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  type MultiSelectOption,
} from "@coval/ui"
import { Sparkles } from "lucide-react"

import type { PersonaMetadata } from "@/types/api/Persona"
import {
  COVAL_VOICES,
  getVoiceNaturalness,
  getVoicePreviewUrl,
  type CovalVoice,
} from "@/types/ModelConfigs/CovalVoiceConstants"
import { cn } from "@/utils/cn"

import type { PersonaCustomVoiceOption } from "../types"
import { voiceSupportsLanguageCode } from "../utils/personaModalUtils"
import {
  getDefaultLanguageCodeForFamily,
  getLanguageDisplayName,
  getLanguageFamily,
  getLanguageFilterOptions,
  getLanguageFlag,
  getPreferredLanguageCodeForVoice,
  getVoiceAccentLabel,
  getVoiceFilterKey,
  getVoiceLanguageSummary,
  getVoiceSearchText,
  VOICE_PITCH_FILTER_OPTIONS,
  VOICE_PRESENTATION_FILTER_OPTIONS,
  voiceMatchesFilters,
  voiceSupportsLanguageFamily,
  type VoiceFilterKey,
} from "./personaVoiceLibraryUtils"
import {
  buildPersonaVoiceColumns,
  getVoiceNaturalnessRank,
  getVoiceRowSortName,
  type PersonaVoiceRow,
} from "./PersonaVoiceTableColumns"
import { SelectedVoiceSummary } from "./SelectedVoiceSummary"

const LIBRARY_ROW_PREFIX = "voice:"
const CUSTOM_ROW_PREFIX = "custom:"

interface PersonaVoiceLibraryProps {
  metadata?: PersonaMetadata
  isBuiltIn: boolean
  isNewPersona?: boolean
  currentlyPlayingAudioId?: string | null
  onVoiceSelection: (selectedVoiceName?: string) => void
  onLanguageSelection: (selectedLanguageCode?: string) => void
  onVoiceVolumeChange?: (volume: number | undefined) => void
  onVoiceSpeedChange?: (speed: number | undefined) => void
  onPlayingChange?: (playingId: string | null) => void
  /** Hides the selected-voice band that sits under the controls. */
  showSelectedSummary?: boolean
  /**
   * Heading hosted at the controls row's leading edge. The picker dialog hands
   * its `DialogTitle` down so the title sits beside the search field rather
   * than in a band above it, the way the metric picker heads its catalog.
   */
  title?: ReactNode
  /** Shape of the library as a whole, e.g. the height it fills in a dialog. */
  className?: string
  customVoices?: PersonaCustomVoiceOption[]
  selectedCustomVoiceId?: string
  customVoicePreviewUrls?: Map<string, string>
  customVoicePreviewErrorIds?: Set<string>
  getCustomVoicePreviewUrl?: (voiceId: string) => string | undefined
  onCustomVoiceSelection?: (voice: PersonaCustomVoiceOption) => void
}

export function PersonaVoiceLibrary({
  metadata,
  isBuiltIn,
  isNewPersona = false,
  currentlyPlayingAudioId,
  onVoiceSelection,
  onLanguageSelection,
  onVoiceVolumeChange,
  onVoiceSpeedChange,
  onPlayingChange,
  showSelectedSummary = true,
  title,
  className = "h-[70vh]",
  customVoices = [],
  selectedCustomVoiceId,
  customVoicePreviewUrls,
  customVoicePreviewErrorIds,
  getCustomVoicePreviewUrl,
  onCustomVoiceSelection,
}: PersonaVoiceLibraryProps) {
  const headingId = useId()
  const [searchQuery, setSearchQuery] = useState("")
  const [isLanguageBroad, setIsLanguageBroad] = useState(isNewPersona)
  const [selectedVoiceFilterKeys, setSelectedVoiceFilterKeys] = useState<
    Set<VoiceFilterKey>
  >(new Set())

  useEffect(() => {
    setIsLanguageBroad(isNewPersona)
  }, [isNewPersona])

  const selectedVoice = useMemo(
    () => COVAL_VOICES.find((voice) => voice.name === metadata?.voice_name),
    [metadata?.voice_name],
  )
  const selectedCustomVoice = customVoices.find(
    (voice) => voice.id === selectedCustomVoiceId,
  )
  const selectedLanguageCode = metadata?.language_code
  const selectedLanguageFamily = selectedLanguageCode
    ? getLanguageFamily(selectedLanguageCode)
    : undefined
  const languageFilterOptions = useMemo(
    () => getLanguageFilterOptions(selectedLanguageCode),
    [selectedLanguageCode],
  )
  // A code with no regional entry of its own (a bare family, or the only
  // region a language has) reads as the language as a whole.
  const isRegionalFilter =
    !isLanguageBroad &&
    languageFilterOptions.some(
      (option) => option.isRegional && option.value === selectedLanguageCode,
    )
  const languageFilterValue = isRegionalFilter
    ? selectedLanguageCode
    : selectedLanguageFamily

  const previewUrlFor = useCallback(
    (voice: CovalVoice) =>
      voice.hasPreview === false
        ? undefined
        : getVoicePreviewUrl(
            voice.name,
            getPreferredLanguageCodeForVoice(
              voice,
              selectedLanguageFamily,
              selectedLanguageCode,
            ),
          ),
    [selectedLanguageCode, selectedLanguageFamily],
  )
  const visibleVoices = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()

    return COVAL_VOICES.filter((voice) => {
      if (
        isRegionalFilter
          ? !voiceSupportsLanguageCode(voice, selectedLanguageCode)
          : !voiceSupportsLanguageFamily(voice, selectedLanguageFamily)
      ) {
        return false
      }

      if (!voiceMatchesFilters(voice, selectedVoiceFilterKeys)) {
        return false
      }

      if (
        normalizedQuery &&
        !getVoiceSearchText(voice).includes(normalizedQuery)
      ) {
        return false
      }

      return true
    })
  }, [
    isRegionalFilter,
    searchQuery,
    selectedLanguageCode,
    selectedLanguageFamily,
    selectedVoiceFilterKeys,
  ])

  // Custom voices sit outside the language and pitch filters — they are the
  // user's own and there is no audited metadata to filter them by — but the
  // search field still has to reach them.
  const visibleCustomVoices = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) {
      return customVoices
    }
    return customVoices.filter((voice) =>
      [voice.name, voice.description, getLanguageDisplayName(voice.language)]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery),
    )
  }, [customVoices, searchQuery])

  const visibleVoiceCount = visibleVoices.length + visibleCustomVoices.length

  const voiceFilterOptions: MultiSelectOption[] = useMemo(
    () => [
      ...VOICE_PRESENTATION_FILTER_OPTIONS.map((option) => ({
        value: getVoiceFilterKey("presentation", option.value),
        label: option.label,
      })),
      ...VOICE_PITCH_FILTER_OPTIONS.map((option) => ({
        value: getVoiceFilterKey("pitch", option.value),
        label: option.label,
      })),
    ],
    [],
  )

  const handleLanguageFamilySelection = (languageFamily: string) => {
    const selectedVoiceSupportsFamily =
      selectedVoice &&
      voiceSupportsLanguageFamily(selectedVoice, languageFamily)
    // A saved language the catalog does not list has no default of its own,
    // so picking it keeps the saved code rather than clearing it.
    const defaultLanguageCode =
      getDefaultLanguageCodeForFamily(languageFamily) ??
      (selectedLanguageFamily === languageFamily
        ? selectedLanguageCode
        : languageFamily)

    setIsLanguageBroad(true)
    onLanguageSelection(
      getPreferredLanguageCodeForVoice(
        selectedVoiceSupportsFamily ? selectedVoice : undefined,
        languageFamily,
        defaultLanguageCode,
      ) ?? defaultLanguageCode,
    )

    if (selectedVoice && !selectedVoiceSupportsFamily) {
      onVoiceSelection(undefined)
    }
  }

  const handleRegionalLanguageSelection = (languageCode: string) => {
    setIsLanguageBroad(false)
    onLanguageSelection(languageCode)

    if (
      selectedVoice &&
      !voiceSupportsLanguageCode(selectedVoice, languageCode)
    ) {
      onVoiceSelection(undefined)
    }
  }

  const handleLanguageFilterSelection = (value: string) => {
    const option = languageFilterOptions.find(
      (candidate) => candidate.value === value,
    )
    if (!option) {
      return
    }

    if (option.isRegional) {
      handleRegionalLanguageSelection(option.value)
    } else {
      handleLanguageFamilySelection(option.value)
    }
  }

  const handleVoiceSelect = (voice: CovalVoice) => {
    if (!isRegionalFilter) {
      const nextLanguageCode = getPreferredLanguageCodeForVoice(
        voice,
        selectedLanguageFamily,
        selectedLanguageCode,
      )
      if (nextLanguageCode && nextLanguageCode !== selectedLanguageCode) {
        onLanguageSelection(nextLanguageCode)
      }
    }

    onVoiceSelection(voice.name)
  }

  const columns = useMemo(
    () =>
      buildPersonaVoiceColumns({
        voiceCount: visibleVoiceCount,
        voiceVolume: metadata?.voice_volume ?? undefined,
        voiceSpeed: metadata?.voice_speed ?? undefined,
        currentlyPlayingAudioId,
        onPlayingChange,
        customVoicePreviewErrorIds,
        getCustomVoicePreviewUrl,
      }),
    [
      currentlyPlayingAudioId,
      customVoicePreviewErrorIds,
      getCustomVoicePreviewUrl,
      metadata?.voice_speed,
      metadata?.voice_volume,
      onPlayingChange,
      visibleVoiceCount,
    ],
  )

  // A custom voice and a library voice can share a name, so the row id carries
  // which catalog the row came from.
  const rows = useMemo<PersonaVoiceRow[]>(
    () => [
      // A custom voice carries the one language it was designed in and no
      // accent of its own, so it fills the columns those headers name and
      // leaves the rest empty rather than borrowing them for its blurb.
      ...visibleCustomVoices.map((voice) => ({
        id: `${CUSTOM_ROW_PREFIX}${voice.id}`,
        name: voice.name,
        sortName: getVoiceRowSortName(voice.name, true),
        description: voice.description,
        presentation: "",
        accent: "",
        languages: getLanguageDisplayName(voice.language),
        naturalnessRank: getVoiceNaturalnessRank(undefined),
        customVoice: voice,
        previewUrl: customVoicePreviewUrls?.get(voice.id),
      })),
      ...visibleVoices.map((voice) => ({
        id: `${LIBRARY_ROW_PREFIX}${voice.name}`,
        name: voice.displayName,
        sortName: getVoiceRowSortName(voice.displayName, false),
        presentation: voice.voicePresentation,
        accent: getVoiceAccentLabel(voice) ?? "",
        languages: getVoiceLanguageSummary(voice),
        naturalness: getVoiceNaturalness(voice.name),
        naturalnessRank: getVoiceNaturalnessRank(
          getVoiceNaturalness(voice.name),
        ),
        voice,
        previewUrl: previewUrlFor(voice),
      })),
    ],
    [customVoicePreviewUrls, previewUrlFor, visibleCustomVoices, visibleVoices],
  )

  const selectedRowId = selectedCustomVoiceId
    ? `${CUSTOM_ROW_PREFIX}${selectedCustomVoiceId}`
    : metadata?.voice_name
      ? `${LIBRARY_ROW_PREFIX}${metadata.voice_name}`
      : undefined
  const selectedRowIds = useMemo(
    () => new Set(selectedRowId ? [selectedRowId] : []),
    [selectedRowId],
  )

  // One voice at a time: whichever row the change added wins, and unchecking
  // the current one is a no-op rather than a way to leave the persona mute.
  const selectRow = (row: PersonaVoiceRow) => {
    if (isBuiltIn) {
      return
    }
    if (row.customVoice) {
      onCustomVoiceSelection?.(row.customVoice)
      return
    }
    if (row.voice) {
      handleVoiceSelect(row.voice)
    }
  }

  const handleSelectionChange = (nextIds: Set<string>) => {
    const addedId = [...nextIds].find((id) => id !== selectedRowId)
    const addedRow = rows.find((row) => row.id === addedId)
    if (addedRow) {
      selectRow(addedRow)
    }
  }

  return (
    <section
      className={cn("flex min-h-0 w-full flex-col", className)}
      aria-labelledby={headingId}
    >
      <h3 id={headingId} className="sr-only">
        Voice library
      </h3>

      {/* Title, the catalog's filters, and search share the header band, the
          way the metric and entity pickers head their catalogs. */}
      <div className="table-pad shrink-0 border-b border-coval-border">
        <div className="flex flex-wrap items-center gap-2">
          {title ? <div className="mr-auto min-w-0">{title}</div> : null}

          <Select
            value={languageFilterValue ?? ""}
            onValueChange={handleLanguageFilterSelection}
            disabled={isBuiltIn}
          >
            <SelectTrigger aria-label="Language" className="w-56 shrink-0">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              {languageFilterOptions.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  // A regional variant sits under the language it narrows.
                  className={option.isRegional ? "pl-7" : undefined}
                >
                  <LanguageOptionLabel
                    languageCode={option.value}
                    label={option.label}
                  />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <MultiSelect
            aria-label="Presentation"
            placeholder="Presentation"
            options={voiceFilterOptions}
            value={Array.from(selectedVoiceFilterKeys)}
            onValueChange={(values) =>
              setSelectedVoiceFilterKeys(new Set(values as VoiceFilterKey[]))
            }
            disabled={isBuiltIn}
            className="w-36 shrink-0"
          />

          <SearchInput
            aria-label="Search voices"
            placeholder="Search voices"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            disabled={isBuiltIn}
            // Beside a title the field takes a fixed width rather than every
            // pixel the row has spare.
            className={title ? "w-72 shrink-0" : "min-w-48 flex-1"}
          />
        </div>
      </div>

      {/* The selection and the catalog share the contrast surface below the
          controls. The platter is the frame, not the scrolling thing: rows
          scroll inside it and under its sticky header, so its border stays put
          instead of riding up out of view. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 bg-surface-contrast px-6 pb-6 pt-3">
        {showSelectedSummary && (
          <div className="shrink-0 rounded-md border border-coval-border bg-surface-default px-3 py-2">
            {selectedCustomVoice ? (
              <div
                role="group"
                aria-label={`Selected voice ${selectedCustomVoice.name}`}
                className="flex min-w-0 items-center gap-2"
              >
                <Sparkles className="size-4 shrink-0 text-coval-contrast" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {selectedCustomVoice.name}
                  </div>
                  <div className="truncate text-xs text-coval-text-muted">
                    {getLanguageDisplayName(selectedCustomVoice.language)} ·
                    Custom
                  </div>
                </div>
              </div>
            ) : selectedVoice ? (
              <SelectedVoiceSummary
                voice={selectedVoice}
                previewUrl={previewUrlFor(selectedVoice)}
                voiceVolume={metadata?.voice_volume ?? undefined}
                voiceSpeed={metadata?.voice_speed ?? undefined}
                isDisabled={isBuiltIn}
                currentlyPlayingAudioId={currentlyPlayingAudioId}
                onVoiceVolumeChange={onVoiceVolumeChange}
                onVoiceSpeedChange={onVoiceSpeedChange}
                onPlayingChange={onPlayingChange}
              />
            ) : (
              <div className="text-sm text-coval-text-muted">
                No voice selected
              </div>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-hidden rounded-md border border-coval-border bg-surface-default">
          <Table<PersonaVoiceRow>
            aria-label="Voice library"
            data={rows}
            columns={columns}
            getRowId={(row) => row.id}
            selectable
            // One voice at a time, so there is nothing to select all of and no
            // range to extend — row checkboxes only, on show from the start.
            selectionVisibility="always"
            selectedIds={selectedRowIds}
            onSelectionChange={handleSelectionChange}
            showSelectAll={false}
            allowRangeSelection={false}
            rowClickSelects
            onRowClick={selectRow}
            isRowSelectionDisabled={isBuiltIn ? () => true : undefined}
            // No hover tint: every row here is a candidate, and the picked one is
            // what the surface is for.
            rowHover={false}
            // Voice names wrap rather than truncate, so a row is as tall as its
            // name and badges need; the virtualizer reads the real height from
            // the DOM instead of assuming a uniform one.
            measureRow={() => true}
            defaultSort={{ columnId: "name", direction: "asc" }}
            emptyState="No voices match these filters."
            // The header row takes its height from its cells' padding, so this
            // instance raises it rather than the primitive raising it for every
            // table in the app.
            className="h-full [&>[role=rowgroup]:first-child>[role=row]]:min-h-11"
          />
        </div>
      </div>
    </section>
  )
}

/** A language or regional variant, flagged by the country it is spoken in. */
function LanguageOptionLabel({
  languageCode,
  label,
}: {
  languageCode: string
  label: string
}) {
  const flag = getLanguageFlag(languageCode)
  return (
    <span className="flex min-w-0 items-center gap-2">
      {flag && (
        <span aria-hidden className="shrink-0 text-base leading-none">
          {flag}
        </span>
      )}
      <span className="min-w-0 truncate">{label}</span>
    </span>
  )
}
